// The brand corner — the logo at the top left — which also runs Workspaces.
//
//   • no extra workspace for this person (and every client with Workspaces
//     switched off) → EXACTLY the logo it always was: click = Home.
//   • exactly one bar (an extra workspace, the main one not theirs) → that
//     workspace's logo and title; click = Home. Nothing to switch.
//   • two or more      → the logo, the current workspace's title and a ▾.
//                         A click lists their workspaces — nothing else is
//                         added. Picking one swaps the bar and leaves the
//                         open tabs and the page as they are.
//
// The main workspace wears the site's own Look and Feel; another wears its
// own logo/title, falling back to the site's. The bar gives up no space.

import { forwardRef, useEffect, useRef, useState } from 'react';
import useWorkspaces, { chooseWorkspace } from '@platform/app-shell/frontend/components/TopNav/useWorkspaces';

// The logo for a workspace: its own, else the site's (Look and Feel), else
// the product's light/dark pair. A site logo shows its dark-mode version when
// one is uploaded; otherwise the one logo sits on a white plate in the dark
// theme (a dark mark on the dark bar vanished).
function BrandLogo({ src, darkSrc, alt, fallbackLight, fallbackDark }) {
  if (src && darkSrc) {
    return (
      <>
        <img src={src} alt={alt} className="tn-brand-logo tn-brand-logo--on-light" />
        <img src={darkSrc} alt={alt} className="tn-brand-logo tn-brand-logo--on-dark" />
      </>
    );
  }
  if (src) return <img src={src} alt={alt} className="tn-brand-logo tn-brand-logo--plate" />;
  return (
    <>
      <img src={fallbackLight} alt={alt} className="tn-brand-logo tn-brand-logo--on-light" />
      <img src={fallbackDark} alt={alt} className="tn-brand-logo tn-brand-logo--on-dark" />
    </>
  );
}

// The outer element carries the forwarded ref: TopNav measures it for the
// More fold (see TopNav.jsx recount).
const WorkspaceBrand = forwardRef(function WorkspaceBrand({ brandName, lookAndFeel, onHome, logoLight, logoDark }, outerRef) {
  const { visible, active } = useWorkspaces();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const siteLogo = lookAndFeel.logoDataUri || null;
  const siteLogoDark = lookAndFeel.logoDarkDataUri || null;
  const siteTitle = lookAndFeel.showTitle ? (lookAndFeel.titleText || brandName) : null;

  const siteButton = (
    <button ref={outerRef} type="button" className="tn-brand" onClick={onHome} title={brandName}>
      <BrandLogo src={siteLogo} darkSrc={siteLogoDark} alt={brandName} fallbackLight={logoLight} fallbackDark={logoDark} />
      {siteTitle && <span className="tn-brand-title">{siteTitle}</span>}
    </button>
  );

  // Off, or only the main bar: the brand button exactly as it always was.
  if (!active || (visible.length < 2 && active.isMain)) return siteButton;

  const logoOf = (w) => (w.isMain ? siteLogo : (w.logo || siteLogo));
  const titleOf = (w) => (w.isMain ? (siteTitle || w.label) : (w.title || w.label));

  // One extra workspace and nothing else: its corner, no menu.
  if (visible.length < 2) {
    return (
      <button ref={outerRef} type="button" className="tn-brand" onClick={onHome} title={brandName}>
        <BrandLogo src={logoOf(active)} darkSrc={active.isMain ? siteLogoDark : null} alt={brandName} fallbackLight={logoLight} fallbackDark={logoDark} />
        <span className="tn-brand-title">{titleOf(active)}</span>
      </button>
    );
  }

  const pick = (id) => {
    setOpen(false);
    if (id !== active.id) chooseWorkspace(id);
  };

  return (
    <div className="tn-ws" ref={(el) => { ref.current = el; if (typeof outerRef === 'function') outerRef(el); else if (outerRef) outerRef.current = el; }}>
      <button
        type="button"
        className={`tn-brand tn-brand--switch ${open ? 'tn-brand--open' : ''}`}
        aria-haspopup="menu"
        aria-expanded={open}
        title={`${titleOf(active)}: switch workspace`}
        onClick={() => setOpen((o) => !o)}
      >
        <BrandLogo src={logoOf(active)} darkSrc={active.isMain ? siteLogoDark : null} alt={brandName} fallbackLight={logoLight} fallbackDark={logoDark} />
        <span className="tn-brand-title">{titleOf(active)}</span>
        <svg className="tn-chevron tn-brand-chevron" width="10" height="10" viewBox="0 0 10 10" aria-hidden="true"><path d="M2 3.5 5 6.5 8 3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></svg>
      </button>
      {open && (
        <div className="tn-dropdown tn-ws-menu" role="menu">
          <div className="tn-ws-head">Workspaces</div>
          {visible.map((w) => (
            <button
              key={w.id}
              type="button"
              role="menuitemradio"
              aria-checked={w.id === active.id}
              className={`tn-menu-item tn-ws-item ${w.id === active.id ? 'tn-ws-item--on' : ''}`}
              onClick={() => pick(w.id)}
            >
              {!w.isMain && w.logo && <img src={w.logo} alt="" className="tn-ws-logo" />}
              <span className="tn-menu-item-label">{w.label}</span>
              {w.id === active.id && <span aria-hidden="true">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

export default WorkspaceBrand;
